import React from "react";
import classNames from "classnames";

import { Context } from "../context";
import { useInfiniteScroll } from "@common/infiniteScroll";
import { Props } from "./types";
import styles from "./styles.scss";

function ScrollBar({
  id,
  orientation,
  scrollSize,
  withInfiniteLoad,
  children,
  scroll,
  updateRef,
}: Props) {
  const infiniteLoadingRef = useInfiniteScroll();
  const [isDragging, setIsDragging] = React.useState(false);
  const basicRef = React.useRef<HTMLDivElement | null>(null);
  const track = withInfiniteLoad ? infiniteLoadingRef : basicRef;
  const { registerGutter, removeGutter } = React.useContext(Context);

  React.useEffect(() => {
    registerGutter({ id, ref: track });

    return () => {
      removeGutter(id);
    };
  }, [id, track.current]);

  React.useEffect(() => {
    updateRef && updateRef(track);
  }, [updateRef, track.current]);

  return (
    <div
      className={classNames(styles.scrollBar, {
        [styles.horizontal]: orientation === "horizontal",
        [styles.vertical]: orientation === "vertical",
        [styles.dragging]: isDragging,
      })}
      onMouseLeave={isDragging ? () => setIsDragging(false) : undefined}
      onMouseUp={isDragging ? () => setIsDragging(false) : undefined}
      onMouseMove={
        isDragging
          ? (e) => {
              if (!track.current) return;

              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const y = e.clientY - rect.top;

              orientation === "horizontal"
                ? track.current.scrollTo((x / rect.width) * scrollSize, 0)
                : track.current.scrollTo(0, (y / rect.height) * scrollSize);
            }
          : undefined
      }
    >
      <div className={styles.content}>{children}</div>
      <div
        ref={track}
        className={styles.track}
        onMouseDown={(e) => {
          if (!track.current) return;

          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;

          orientation === "horizontal"
            ? track.current.scrollTo((x / rect.width) * scrollSize, 0)
            : track.current.scrollTo(0, (y / rect.height) * scrollSize);

          setIsDragging(true);
        }}
        onScroll={(e) =>
          scroll(
            orientation === "vertical"
              ? e.currentTarget.scrollTop
              : e.currentTarget.scrollLeft,
          )
        }
      >
        <div>
          <div
            className={styles.thumb}
            style={
              orientation === "horizontal"
                ? { width: scrollSize }
                : { height: scrollSize }
            }
          />
        </div>
      </div>
    </div>
  );
}

export { ScrollBar };
